import { Currency, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { getAddonClientMax, getSoldOutLimitError } from "common";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { AddOn, CatalogPriceType, CatalogTemplateItem, LineItem, ProductCatalogItem } from "types";
import { getCatalogItemPhoto } from "../../../models/Catalog";
import { AddonLineItem, CatalogLineItem } from "../../../stores/createPurchaseStore";
import { CatalogItemImage } from "../../Common/CatalogItemImage";
import { AddOnLimitOverrideSwitch } from "../AddOnLimitOverrideSwitch";
import { AddonNameField, getAddonRowPadding } from "./QuantityAddons";
import { isQuestionnaireAddOn } from "@/models/CatalogItem";
import { getSeller } from "@/utils/getSeller";

interface BooleanAddonProps {
    shouldShowDefaultImg?: boolean;
    shouldDecreaseOnly?: boolean;
    isOverriding?: boolean;
    catalogItem: AddOn;
    templateItem: CatalogTemplateItem;
    lineItems: LineItem[] | CatalogLineItem[] | AddonLineItem[];
    limitError?: string;
    /** The most this item may hold under the per event limit at the last Reserve, its own units included. */
    catalogItemOpen?: number;
    onChange: (value: number, id: ID | undefined) => void;
    onOverrideChange?: (value: boolean) => void;
}

export const BooleanAddon = ({
    shouldShowDefaultImg = true,
    shouldDecreaseOnly = false,
    isOverriding = false,
    catalogItem,
    templateItem,
    lineItems,
    limitError,
    catalogItemOpen,
    onChange,
    onOverrideChange,
}: BooleanAddonProps) => {
    const lineItem = (lineItems as CatalogLineItem[]).find(
        (lineItem) => lineItem.template && lineItem.template.id === templateItem.id,
    );
    // A checkbox add-on takes only the per event limit, so no guest count applies.
    const clientMax = getAddonClientMax({ isOverriding, catalogItem, guestCount: 0, catalogItemOpen });
    const isSoldOut = Boolean(lineItem?.quantity) && clientMax === 0;
    const shownLimitError = getSoldOutLimitError(limitError, isSoldOut);

    const handleChange = (value: boolean) => {
        onChange(value ? 1 : 0, templateItem?.id);
    };

    return (
        <BooleanAddonItem
            shouldShowDefaultImg={shouldShowDefaultImg}
            isChecked={!!lineItem?.quantity}
            shouldDecreaseOnly={shouldDecreaseOnly}
            isOverriding={isOverriding}
            catalogItem={catalogItem}
            limitError={shownLimitError}
            onOverrideChange={onOverrideChange}
            onChange={handleChange}
        />
    );
};

interface BooleanAddonItemProps {
    shouldShowDefaultImg: boolean;
    isChecked: boolean;
    shouldDecreaseOnly: boolean;
    isOverriding?: boolean;
    isLimitExceeded?: boolean;
    catalogItem: ProductCatalogItem;
    error?: string;
    limitError?: string;
    onChange: (value: boolean) => void;
    onOverrideChange?: (value: boolean) => void;
}

export const BooleanAddonItem = ({
    shouldShowDefaultImg,
    isChecked: initialValue,
    shouldDecreaseOnly,
    isOverriding = false,
    isLimitExceeded,
    catalogItem,
    error,
    limitError,
    onChange,
    onOverrideChange,
}: BooleanAddonItemProps) => {
    const seller = getSeller();
    const [isChecked, setIsChecked] = useState(initialValue);
    const isFormAddon = isQuestionnaireAddOn(catalogItem);
    const photoSource = getCatalogItemPhoto(catalogItem);
    const isDefaultProductImgVisible = Boolean(photoSource) || Boolean(shouldShowDefaultImg);
    const rowPadding = getAddonRowPadding(isDefaultProductImgVisible, isFormAddon);

    const handleChange = (value: boolean) => {
        setIsChecked(value);
        onChange(value);
    };

    return (
        <div data-testid="boolean-addon-item" className={clsx("flex w-full items-center", rowPadding)}>
            {/* todo: @srisha add image uri to lineItem or template to be able to display it */}
            {isDefaultProductImgVisible && (
                <CatalogItemImage catalogItem={catalogItem} size={isFormAddon ? "small" : "medium"} />
            )}
            <div className="w-full flex-1">
                <AddonNameField
                    isFormAddon={isFormAddon}
                    isError={Boolean(limitError)}
                    error={error}
                    catalogItem={catalogItem}
                />
                {!isEmpty(catalogItem.prices) && (
                    <div className="text-gray-darker">
                        {catalogItem.prices?.price?.priceType === CatalogPriceType.PERCENT ? (
                            <span>
                                <var data-var="percentAddonPrice">{catalogItem.prices?.price?.min}</var>% of purchasable
                                items
                            </span>
                        ) : (
                            <>
                                <var data-var="absoluteAddonPrice">
                                    <Currency currency={seller.currency}>{catalogItem.prices?.price?.min}</Currency>
                                </var>
                                / add-on
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className="relative">
                <AddOnLimitOverrideSwitch
                    isLimitExceeded={isLimitExceeded}
                    limitOverride={{ limitError, isOverriding, setIsOverriding: onOverrideChange }}
                    restrictions={catalogItem.restrictions ?? []}
                />
                <Switch
                    data-testid="boolean-toggle-button"
                    isChecked={isChecked}
                    size="medium"
                    disabled={shouldDecreaseOnly && !isChecked}
                    onChange={handleChange}
                />
            </div>
        </div>
    );
};
